import React from 'react';
import PropTypes from 'prop-types';
import { caseGuides, overviewGuide } from './caseGuides';
import { OVERVIEW_TAB } from './CaseFieldTabs';

// 탭에 맞는 작성 가이드(쓸 것, 피할 것, 예시).
export function GuideContent({ guide }) {
    return <>
        <h3>이 항목에 쓸 것</h3>
        <ul className="case-guide-list">{guide.focus.map(item => <li key={item}>{item}</li>)}</ul>
        <h3>피할 것</h3>
        <ul className="case-guide-list avoid">{guide.avoid.map(item => <li key={item}>{item}</li>)}</ul>
        {guide.example && <details className="case-example"><summary>작성 예시 보기</summary><p>{guide.example}</p></details>}
    </>;
}

// 서버 평가 라벨을 화면 문구로 바꾼다. 모르는 라벨은 그대로 보여준다.
const LABEL_TEXT = { partial: '일부 충족' };

// 피드백 목록 한 묶음. 비어 있으면 그리지 않는다.
function FeedbackList({ title, items, className }) {
    if (!items?.length) return null;
    return <><h3>{title}</h3><ul className={`case-guide-list ${className}`}>{items.map(item => <li key={item}>{item}</li>)}</ul></>;
}

// 항목 피드백 본문(라벨, 총평, 드러난 내용, 다시 확인할 가이드). 마이페이지에서도 쓴다.
export function FieldFeedbackBody({ data }) {
    return <>
        <span className={`review-tag case-feedback-label ${data.label}`}>{LABEL_TEXT[data.label] || data.label}</span>
        <p className="case-feedback-summary">{data.note}</p>
        {data.revealSummary && <><h3>면담에서 드러난 내용</h3><p className="case-feedback-text">{data.revealSummary}</p></>}
        <FeedbackList title="다시 확인할 가이드" items={data.guideReminders} className="improve" />
    </>;
}

// 저장 후 받은 AI 피드백의 진행 상태와 결과.
function FeedbackContent({ feedback, currentText, onRetry }) {
    if (feedback.status === 'loading') return <p className="ai-small" role="status">AI가 작성한 내용을 읽고 있습니다. 잠시만 기다려주세요.</p>;
    if (feedback.status === 'error') return <div className="ai-alert" role="alert">{feedback.error}{feedback.retryable && <> <button type="button" onClick={onRetry}>다시 시도</button></>}</div>;
    const { data } = feedback;
    const evidenceCount = data.evidenceMessageIndexes.length;
    return <>
        <FieldFeedbackBody data={data} />
        {evidenceCount > 0 && <p className="ai-small case-evidence-note">근거가 된 대화 {evidenceCount}개를 면담 기록에 표시했습니다.</p>}
        {feedback.text !== currentText.trim() && <p className="ai-small case-outdated">피드백을 받은 뒤 내용을 고쳤습니다. 다시 저장하면 새 피드백을 받습니다.</p>}
        <p className="ai-small">AI가 작성 내용을 읽고 만든 학습용 피드백입니다. 정답이나 평가 결과가 아닙니다.</p>
    </>;
}

// 오른쪽 패널. 피드백이 있으면 피드백을 먼저, 가이드는 접어서 보여준다.
export default function CaseGuidePanel({ activeKey, feedback, currentText, onRetry }) {
    const guide = activeKey === OVERVIEW_TAB ? overviewGuide : caseGuides[activeKey];
    return <aside className="review-card case-guide" aria-label="작성 가이드와 AI 피드백">
        <span className="ai-kicker">{feedback ? 'AI FEEDBACK' : 'GUIDE'}</span>
        <h2>{guide.tabLabel} · {feedback ? 'AI 피드백' : '작성 가이드'}</h2>
        <div aria-live="polite">{feedback && <FeedbackContent feedback={feedback} currentText={currentText} onRetry={onRetry} />}</div>
        {feedback
            ? <details className="case-guide-more"><summary>작성 가이드 다시 보기</summary><GuideContent guide={guide} /></details>
            : <GuideContent guide={guide} />}
        {!feedback && activeKey !== OVERVIEW_TAB && <p className="ai-small case-guide-hint">작성한 뒤 저장하면 이 자리에 AI 피드백이 나타납니다.</p>}
    </aside>;
}

const guideShape = PropTypes.shape({ tabLabel: PropTypes.string, focus: PropTypes.arrayOf(PropTypes.string).isRequired, avoid: PropTypes.arrayOf(PropTypes.string).isRequired, example: PropTypes.string });
const fieldFeedbackShape = PropTypes.shape({ label: PropTypes.string, note: PropTypes.string, revealSummary: PropTypes.string, guideReminders: PropTypes.arrayOf(PropTypes.string), evidenceMessageIndexes: PropTypes.arrayOf(PropTypes.number) });
const feedbackShape = PropTypes.shape({
    status: PropTypes.oneOf(['loading', 'done', 'error']).isRequired,
    text: PropTypes.string,
    error: PropTypes.string,
    retryable: PropTypes.bool,
    data: fieldFeedbackShape,
});

GuideContent.propTypes = { guide: guideShape.isRequired };
FieldFeedbackBody.propTypes = { data: fieldFeedbackShape.isRequired };
FeedbackList.propTypes = { title: PropTypes.string.isRequired, items: PropTypes.arrayOf(PropTypes.string), className: PropTypes.string.isRequired };
FeedbackContent.propTypes = { feedback: feedbackShape.isRequired, currentText: PropTypes.string.isRequired, onRetry: PropTypes.func.isRequired };
CaseGuidePanel.propTypes = { activeKey: PropTypes.string.isRequired, feedback: feedbackShape, currentText: PropTypes.string.isRequired, onRetry: PropTypes.func.isRequired };
